/* Opt-in enhancement of the existing product sizing accordion. No product data is stored here. */
(() => {
  if (window.LoveCashmereSizeGuide) return;

  const rootSelector = 'product-info[data-lc-size-guide-enabled]';
  const sizePattern = /^(?:XXS|XS|S|M|L|XL|XXL|XXXL|[2-5]XL)(?:\s*\/\s*(?:XS|S|M|L|XL|XXL))?(?:\s*\([^\n]+\))?$/i;

  // Keep DOM positions so the introduction can be cloned without rewriting its markup.
  function readLines(source) {
    const lines = [];
    let text = '';
    let start = null;
    const flush = () => {
      if (text.trim()) lines.push({ text: text.trim().replace(/\s+/g, ' '), start });
      text = '';
      start = null;
    };
    const visit = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const first = node.textContent.search(/\S/);
        if (!start && first !== -1) start = { node, offset: first };
        text += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.tagName === 'BR') return flush();
        const block = /^(DIV|P|H[1-6]|LI|UL|OL)$/.test(node.tagName);
        if (block) flush();
        node.childNodes.forEach(visit);
        if (block) flush();
      }
    };
    source.childNodes.forEach(visit);
    flush();
    return lines;
  }

  function formatMeasurements(content) {
    const source = content.querySelector('.metafield-rich_text_field');
    if (!source || source.dataset.lcSizeGuideSource !== undefined || source.querySelector('table')) return;
    const lines = readLines(source);
    const firstSize = lines.findIndex((line) => sizePattern.test(line.text));
    if (firstSize < 0) return;
    const rows = [];
    let row;
    // Only transform complete, consistently labelled measurement groups. Unknown content stays intact.
    for (const line of lines.slice(firstSize)) {
      if (sizePattern.test(line.text)) {
        row = { size: line.text, measurements: [] };
        rows.push(row);
      } else {
        const match = line.text.match(/^([^:]+):\s*(.+)$/);
        if (!row || !match) return;
        row.measurements.push({ label: match[1].trim(), value: match[2].trim() });
      }
    }
    if (rows.length < 2 || rows[0].measurements.length < 2) return;
    const labels = rows[0].measurements.map((measurement) => measurement.label);
    if (new Set(labels).size !== labels.length || new Set(rows.map((item) => item.size)).size !== rows.length) return;
    if (rows.some((item) => item.measurements.length !== labels.length || item.measurements.some((measurement, index) => measurement.label !== labels[index]))) return;

    const formatted = document.createElement('div');
    formatted.className = 'lc-size-guide__formatted';
    const intro = document.createElement('div');
    intro.className = 'lc-size-guide__intro';
    const range = document.createRange();
    range.setStart(source, 0);
    range.setEnd(lines[firstSize].start.node, lines[firstSize].start.offset);
    intro.append(range.cloneContents());
    // A range ending inside the first size heading can leave empty formatting elements.
    [...intro.querySelectorAll('*')].reverse().forEach((element) => {
      if (element.tagName !== 'BR' && !element.textContent.trim() && !element.querySelector('br, img, svg')) element.remove();
    });
    if (intro.textContent.trim()) formatted.append(intro);
    const scroll = document.createElement('div');
    scroll.className = 'lc-size-guide__scroll';
    scroll.tabIndex = 0;
    scroll.setAttribute('role', 'region');
    scroll.setAttribute('aria-label', 'Garment measurements; scroll horizontally for all measurements');
    const table = document.createElement('table');
    table.className = 'lc-size-guide__table';
    const caption = table.createCaption();
    caption.textContent = 'Garment measurements';
    const header = table.createTHead().insertRow();
    ['Size', ...labels].forEach((label) => {
      const cell = document.createElement('th');
      cell.scope = 'col';
      cell.textContent = label;
      header.append(cell);
    });
    const body = table.createTBody();
    rows.forEach((item) => {
      const tr = body.insertRow();
      const heading = document.createElement('th');
      heading.scope = 'row';
      heading.textContent = item.size;
      tr.append(heading);
      item.measurements.forEach((measurement) => { tr.insertCell().textContent = measurement.value; });
    });
    scroll.append(table);
    formatted.append(scroll);
    source.before(formatted);
    source.dataset.lcSizeGuideSource = '';
    source.hidden = true;
    // Formatting may run while a merchant has the accordion open in the editor.
    if (content.closest('details')?.open && content.style.height) content.style.height = 'auto';
  }

  function syncControl(root) {
    const guide = root.querySelector('[data-lc-size-guide]');
    if (!guide) return;
    const summary = guide.querySelector('summary');
    const expanded = summary?.getAttribute('aria-expanded') ?? String(guide.open);
    root.querySelectorAll('.lc-size-guide-link').forEach((control) => {
      if (control.getAttribute('aria-expanded') !== expanded) control.setAttribute('aria-expanded', expanded);
    });
  }

  function initialise(root) {
    const guide = root.querySelector('[data-lc-size-guide]');
    if (!guide || !guide.id) return;
    const content = guide.querySelector('[data-lc-size-guide-content]');
    if (content) formatMeasurements(content);
    const option = [...root.querySelectorAll('variant-selects [data-option-name]')].find((element) => element.dataset.optionName.trim().toLowerCase() === 'size');
    const group = option?.closest('.product-form__input');
    const label = group?.querySelector('legend, label.form__label');
    if (!label) return;
    if (!group.querySelector('.lc-size-guide-link')) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'lc-size-guide-link';
      button.textContent = 'Size guide';
      button.setAttribute('aria-controls', guide.id);
      if (label.tagName === 'LEGEND') {
        label.classList.add('lc-size-guide__option-label');
        label.append(button);
      } else {
        // Do not place another interactive control inside a dropdown's associated label.
        const header = document.createElement('div');
        header.className = 'lc-size-guide__option-label';
        label.before(header);
        header.append(label, button);
      }
    }
    syncControl(root);
  }

  function init(scope = document) {
    if (scope.matches?.(rootSelector)) initialise(scope);
    scope.querySelectorAll?.(rootSelector).forEach(initialise);
  }

  document.addEventListener('click', (event) => {
    const control = event.target.closest?.('.lc-size-guide-link');
    if (!control) return;
    const root = control.closest(rootSelector);
    const guide = root?.querySelector('[data-lc-size-guide]');
    const summary = guide?.querySelector('summary');
    if (!summary) return;
    event.preventDefault();
    const expanded = summary.getAttribute('aria-expanded');
    if (expanded === 'false' || (expanded === null && !guide.open)) summary.click();
    // Retain the sticky purchase block, including its price/payment controls, above the guide.
    summary.style.removeProperty('--lc-size-guide-scroll-offset');
    let scrollOffset = parseFloat(getComputedStyle(summary).scrollMarginTop) || 0;
    root.querySelectorAll('.buy-buttons').forEach((buttons) => {
      const style = getComputedStyle(buttons);
      const top = parseFloat(style.top);
      if (style.position === 'sticky' && Number.isFinite(top)) {
        scrollOffset = Math.max(scrollOffset, top + buttons.getBoundingClientRect().height + 16);
      }
    });
    summary.style.setProperty('--lc-size-guide-scroll-offset', `${scrollOffset}px`);
    summary.focus({ preventScroll: true });
    summary.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    syncControl(root);
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  const observer = new MutationObserver((records) => {
    const roots = new Set();
    for (const record of records) {
      if (record.type === 'attributes') {
        const root = record.target.closest(rootSelector);
        if (root) syncControl(root);
        continue;
      }
      const root = record.target.closest?.(rootSelector);
      if (root) roots.add(root);
      record.addedNodes.forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (node.matches(rootSelector)) roots.add(node);
        node.querySelectorAll(rootSelector).forEach((item) => roots.add(item));
      });
    }
    roots.forEach(initialise);
  });
  const start = () => {
    init();
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'aria-expanded'] });
  };
  window.LoveCashmereSizeGuide = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
